// KPIs da seção Operação (saúde do pipeline): Falhas (tom
// destrutivo acima do limiar), Em fila agora e Agendados futuros —
// com ícones semânticos. Cada card é clicável → Consulta
// pré-filtrada ("agora"/"futuros" não têm recorte de data).
import {
  AlertTriangleIcon,
  CalendarClockIcon,
  Clock3Icon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { NotificationOverviewKpisType } from "@/types/notification-center"

import { formatDashboardInteger } from "../dashboard-utils"
import { DashboardKpiCard } from "./dashboard-kpi-card"

// Limiar do tom destrutivo do KPI Falhas: falhas/enviados > 5%.
const FAILURES_ALERT_RATE = 0.05

interface DashboardOperationKpisProps {
  kpis?: NotificationOverviewKpisType
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  // Janela selecionada resolvida (startDate/endDate) — deep-links.
  range: { startDate?: string; endDate?: string }
  // Rótulo do delta (vs dia/período anterior) — vem do container.
  deltaLabel: string
}

export const DashboardOperationKpis = ({
  kpis,
  isLoading,
  hasError,
  onRetry,
  range,
  deltaLabel,
}: DashboardOperationKpisProps) => {
  const { t, i18n } = useTranslation()
  const dashboardPrefix =
    "pages.notificationCenter.dashboardPage"
  const kpisPrefix = `${dashboardPrefix}.kpis`

  if (isLoading) {
    return (
      <div
        data-testid="dashboard-operation-kpis-skeleton"
        className="grid gap-4 @3xl:grid-cols-3"
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`dashboard-operation-kpi-skeleton-${index}`}
            className="h-28 w-full"
          />
        ))}
      </div>
    )
  }
  if (hasError || !kpis) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${dashboardPrefix}.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={onRetry}
        >
          {t(`${dashboardPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  const isFailuresHigh =
    kpis.sent.value > 0 &&
    kpis.failures.value / kpis.sent.value >
      FAILURES_ALERT_RATE

  return (
    <div className="grid gap-4 @3xl:grid-cols-3">
      <DashboardKpiCard
        icon={AlertTriangleIcon}
        tone={isFailuresHigh ? "destructive" : "warning"}
        label={t(`${kpisPrefix}.failures`)}
        value={formatDashboardInteger(
          kpis.failures.value,
          i18n.language
        )}
        delta={kpis.failures.delta}
        deltaLabel={deltaLabel}
        isBadWhenUp
        filters={{ status: "FAILED", ...range }}
        ariaLabel={t(`${kpisPrefix}.links.failures`)}
      />
      <DashboardKpiCard
        icon={Clock3Icon}
        tone="info"
        label={t(`${kpisPrefix}.queuedNow`)}
        value={formatDashboardInteger(
          kpis.queuedNow.value,
          i18n.language
        )}
        delta={kpis.queuedNow.delta}
        deltaLabel={deltaLabel}
        isBadWhenUp
        filters={{ status: "QUEUED", period: "all" }}
        ariaLabel={t(`${kpisPrefix}.links.queuedNow`)}
      />
      <DashboardKpiCard
        icon={CalendarClockIcon}
        tone="primary"
        label={t(`${kpisPrefix}.scheduledFuture`)}
        value={formatDashboardInteger(
          kpis.scheduledFuture.value,
          i18n.language
        )}
        delta={kpis.scheduledFuture.delta}
        deltaLabel={deltaLabel}
        filters={{ scheduled: "true", period: "all" }}
        ariaLabel={t(`${kpisPrefix}.links.scheduledFuture`)}
      />
    </div>
  )
}
